/**
 * Higher-timeframe (HTF) curves for the BBR and Wave oscillators.
 *
 * Draws the oscillator of the Strategies-mapped higher timeframe on the chart's
 * own (lower) timeframe, in the same pane. Two pure steps, both here so they can
 * be tested off the chart (`backend/src/scripts/verifyOscHtf.ts`):
 *
 *   1. `buildHtfBarsForOsc` — the HTF bars to compute on: every CLOSED HTF bar,
 *      plus the still-forming one REBUILT from the chart's own lower-timeframe
 *      bars, so the HTF curve moves with every tick rather than only when the
 *      HTF series is re-fetched (every 5 minutes).
 *   2. `stampOscHtf` — each LTF bar gets the value of the HTF bar COVERING it,
 *      so a daily value is drawn flat across that day's hourly bars.
 *
 * ALIGNED, NOT CAUSAL — BY THE OWNER'S CHOICE (2026-09-24). A closed HTF value
 * sits across its own window, the way a trader reads an HTF oscillator. In
 * history that value was only final when the window closed, so this is for
 * READING. It feeds no signal: the BW / BD / X marks stay on the chart's own
 * timeframe exactly as measured. Anything that ever decides on it must use
 * `stampHtfLevels` (utils/clhBand.ts) instead, which only sees closed bars.
 *
 * BAR REPLAY. The fetched HTF series is NOT cut to the playhead, and the HTF bar
 * containing the playhead carries highs and lows from the future. So in replay
 * only HTF bars that had closed by the end of the playhead bar are kept, and the
 * forming one is rebuilt purely from the LTF bars on screen — which the replay
 * has already cut.
 *
 * JOINED ON BAR TIME, NEVER ON INDEX. Times are the chart's IST-shifted seconds
 * throughout, the same frame `dropFormingHtfBars` works in.
 */
import type { CandleRow } from '../components/charts/TradingChart';
import { isCalendarInterval, nextBucketStart } from './intervalCatalog';

/** When an HTF bar that opened at `open` closes. A calendar month is 28-31
 *  days, so 1M asks the catalog; fixed sizes are plain arithmetic, exactly as
 *  `dropFormingHtfBars` does it. */
export function htfBarCloseTime(open: number, htfMin: number): number {
  return isCalendarInterval(htfMin) ? nextBucketStart(open, htfMin) : open + htfMin * 60;
}

export interface HtfBarsForOsc {
  /** Closed HTF bars, then (optionally) the forming one. */
  bars: CandleRow[];
  /** How many leading `bars` are CLOSED. `bars.length - closedLen` is 0 or 1. */
  closedLen: number;
}

export interface BuildHtfOptions {
  /** The chart's interval in minutes — how long the playhead bar lasts. */
  ltfMin: number;
  /** Bar-replay playhead (an LTF bar time), or null when live. */
  replayCut?: number | null;
  /** IST-shifted "now" in seconds. Only for tests; defaults to the clock. */
  nowSec?: number;
}

/**
 * @param htfRaw  the fetched mapped-HTF bars, ascending (auxCandles.srHtf)
 * @param htfMin  the HTF size in minutes
 * @param ltfBars the chart's REAL lower-timeframe bars, ascending, including the
 *                live bar when there is one (TradingChart's `bwRealBars`)
 */
export function buildHtfBarsForOsc(
  htfRaw: CandleRow[],
  htfMin: number,
  ltfBars: CandleRow[],
  opts: BuildHtfOptions,
): HtfBarsForOsc {
  if (!htfRaw?.length || !(htfMin > 0)) return { bars: [], closedLen: 0 };
  const replay = opts.replayCut != null && Number.isFinite(opts.replayCut);
  const ltfSec = Math.max(1, Number(opts.ltfMin) || 1) * 60;
  // The moment "now" is: the end of the playhead bar in replay, the clock live.
  const asOf = replay
    ? Number(opts.replayCut) + ltfSec
    : (opts.nowSec ?? Math.floor(Date.now() / 1000) + 19800);

  const closed: CandleRow[] = [];
  for (const b of htfRaw) {
    const t = Number(b.time);
    if (htfBarCloseTime(t, htfMin) <= asOf) closed.push(b);
    else break;
  }

  // Where the forming HTF bar opens. Prefer the fetched bar's own time (it
  // knows the venue's session anchor); otherwise the first LTF bar at or after
  // the last closed bar's close — right across a weekend or session gap, where
  // "previous close" is not when the next bar opened.
  const lastClose = closed.length
    ? htfBarCloseTime(Number(closed[closed.length - 1].time), htfMin)
    : -Infinity;
  const fetched = htfRaw[closed.length];
  let formStart: number | null = null;
  if (fetched && Number(fetched.time) <= asOf) formStart = Number(fetched.time);
  else {
    const firstAfter = (ltfBars || []).find(c => Number(c.time) >= lastClose && Number(c.time) < asOf);
    if (firstAfter) formStart = Number(firstAfter.time);
  }
  if (formStart == null) return { bars: closed, closedLen: closed.length };
  const formEnd = htfBarCloseTime(formStart, htfMin);

  // The LTF bars inside the forming window, up to "now".
  const inside = (ltfBars || []).filter(c => {
    const t = Number(c.time);
    return t >= formStart! && t < formEnd && t < asOf;
  });

  // Live: start from the fetched bar (it may predate the chart's first LTF bar)
  // and fold every newer LTF print in. Replay: the fetched bar is the future,
  // so only the LTF bars count.
  const base = !replay && fetched && Number(fetched.time) === formStart ? fetched : null;
  if (!base && !inside.length) return { bars: closed, closedLen: closed.length };
  let open = base ? Number(base.open) : Number(inside[0].open);
  let high = base ? Number(base.high) : -Infinity;
  let low = base ? Number(base.low) : Infinity;
  let close = base ? Number(base.close) : Number(inside[0].close);
  for (const c of inside) {
    high = Math.max(high, Number(c.high));
    low = Math.min(low, Number(c.low));
    close = Number(c.close);
  }
  if (!base && inside.length) open = Number(inside[0].open);
  const forming = { ...(base || inside[0]), time: formStart, open, high, low, close } as CandleRow;
  return { bars: [...closed, forming], closedLen: closed.length };
}

export interface OscHtfStamp {
  /** The LTF bar's time. */
  time: number;
  /** The covering HTF bar's oscillator value. */
  value: number;
  /** The covering HTF bar had not closed yet — its value still moves. */
  forming: boolean;
}

/**
 * Stamp each LTF bar with the value of the HTF bar covering it.
 *
 * @param ltfTimes ascending LTF bar times (the bars on screen)
 * @param htfBars  the bars `values` was computed on (from buildHtfBarsForOsc)
 * @param values   one oscillator value per HTF bar, null during warm-up
 */
export function stampOscHtf(
  ltfTimes: number[],
  htfBars: Array<{ time: unknown }>,
  values: ReadonlyArray<number | null | undefined>,
  htfMin: number,
  closedLen: number,
): OscHtfStamp[] {
  const out: OscHtfStamp[] = [];
  if (!ltfTimes?.length || !htfBars?.length || !(htfMin > 0)) return out;
  let k = -1;
  for (const t of ltfTimes) {
    while (k + 1 < htfBars.length && Number(htfBars[k + 1].time) <= t) k++;
    if (k < 0) continue;                                   // before the first HTF bar
    const forming = k >= closedLen;
    // Past a bar's window there is no covering bar (a data gap) — the forming
    // bar included. Its window is known; leaving it open-ended painted one
    // value across every later LTF bar whenever the fetched HTF series was
    // stale, which verifyOscHtf caught.
    if (t >= htfBarCloseTime(Number(htfBars[k].time), htfMin)) continue;
    const v = values[k];
    if (v == null || !Number.isFinite(Number(v))) continue;
    out.push({ time: t, value: Number(v), forming });
  }
  return out;
}
